// i18n configuration constants.
//
// The supported-language list is BUILD-TIME configured via `VITE_SUPPORTED_LANG`
// (comma-separated ISO 639-1 codes, e.g. "en,es") so adding a language is a
// env-edit + redeploy, no code change. This list MUST stay in sync with the
// BE's `config.yaml` `i18n.supportedLanguages` — otherwise the FE will offer
// a language the BE has no notification copy / error translations for.
//
// See `docs/i18n.md`.

const rawSupported = import.meta.env.VITE_SUPPORTED_LANG ?? 'en';

/**
 * Authoritative list of language codes the FE will accept from any source —
 * Profile.language, `?l=` query param, localStorage, navigator.language.
 * Any value not in this list is filtered out at the detection chain layer
 * (see `src/i18n/index.ts`), so downstream code can trust `i18n.language`
 * is always one of these.
 */
export const SUPPORTED_LANGUAGES: string[] = rawSupported
  .split(',')
  .map((s: string) => s.trim().toLowerCase())
  .filter((s: string): boolean => s.length > 0);

/**
 * Fallback language used when no other signal resolves. Matches the BE's
 * `config.yaml` `i18n.defaultLanguage`.
 */
export const DEFAULT_LANGUAGE = 'en';

/**
 * True when the given code is in SUPPORTED_LANGUAGES. Case-insensitive on
 * input — callers can pass `Profile.language` or `navigator.language`
 * fragments without pre-normalizing. Returns false on null/undefined/empty
 * so consumers can guard cleanly:
 *
 *   if (isSupportedLanguage(profile.language)) { i18n.changeLanguage(profile.language) }
 */
export function isSupportedLanguage(code?: string | null): boolean {
  if (!code) return false;
  return SUPPORTED_LANGUAGES.includes(code.toLowerCase());
}
